import { useCallback, useMemo } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import type { FileExplorerSearchParams } from "@/features/categories/types";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import type { ProfileQueryRequest } from "@/services/api/companies";
import {
  COLUMN_TO_SORT_FIELD_MAP,
  SORT_FIELD_TO_COLUMN_MAP,
} from "@/features/categories/components/category-views/company-staff/columnConfig";

export function useCompanyStaffSearchParams() {
  const search = useSearch({
    from: "/file-explorer/$categoryName",
  }) as FileExplorerSearchParams;
  const navigate = useNavigate({ from: "/file-explorer/$categoryName" });

  const pagination = useMemo<PaginationState>(() => {
    const page = search.staffPage ? Number(search.staffPage) : 1;
    const pageSize = search.staffPageSize ? Number(search.staffPageSize) : 30;
    return {
      pageIndex: Math.max(0, page - 1),
      pageSize: Math.max(1, pageSize),
    };
  }, [search.staffPage, search.staffPageSize]);

  const sorting = useMemo<SortingState>(() => {
    if (!search.staffSortBy) return [{ id: "experienceCount", desc: true }];
    const sortField =
      search.staffSortBy as keyof typeof SORT_FIELD_TO_COLUMN_MAP;
    const columnId = SORT_FIELD_TO_COLUMN_MAP[sortField] || search.staffSortBy;
    const desc = search.staffSortDirection === "DESC";
    return [{ id: String(columnId), desc }];
  }, [search.staffSortBy, search.staffSortDirection]);

  const filters = useMemo<Partial<ProfileQueryRequest>>(() => {
    const filterKeys: (keyof ProfileQueryRequest)[] = [
      "searchColumn",
      "searchTerm",
      "department",
      "country",
      "seniority",
      "minF1ExperienceYears",
      "maxF1ExperienceYears",
      "minTotalExperienceYears",
      "maxTotalExperienceYears",
      "maxFakeProbability",
    ];

    const parsedFilters: Partial<ProfileQueryRequest> = {};

    filterKeys.forEach((key) => {
      let urlKey: string;
      if (key === "searchColumn") {
        urlKey = "staffSearchColumn";
      } else if (key === "searchTerm") {
        urlKey = "staffSearchTerm";
      } else {
        urlKey = `staff${key.charAt(0).toUpperCase() + key.slice(1)}`;
      }

      const value = search[urlKey as keyof typeof search];
      if (value !== undefined && value !== "" && value !== null) {
        if (key === "searchColumn") {
          parsedFilters[key] = value as ProfileQueryRequest["searchColumn"];
        } else if (key === "searchTerm") {
          if (typeof value === "string" && value) {
            parsedFilters[key] = value.split("||");
          }
        } else if (
          key === "minF1ExperienceYears" ||
          key === "maxF1ExperienceYears" ||
          key === "minTotalExperienceYears" ||
          key === "maxTotalExperienceYears" ||
          key === "maxFakeProbability"
        ) {
          parsedFilters[key] = Number(value);
        } else if (typeof value === "string") {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (parsedFilters as any)[key] = value;
        }
      }
    });

    return parsedFilters;
  }, [search]);

  const updateSearchParams = useCallback(
    (updates: {
      pagination?: PaginationState;
      sorting?: SortingState;
      filters?: Partial<ProfileQueryRequest>;
    }) => {
      const newSearch: Record<string, string | number | undefined> = {
        ...search,
      };

      if (updates.pagination) {
        const page = updates.pagination.pageIndex + 1;
        newSearch.staffPage = page > 1 ? String(page) : "";
        newSearch.staffPageSize =
          updates.pagination.pageSize !== 30
            ? String(updates.pagination.pageSize)
            : "";
      }

      if (updates.sorting) {
        if (updates.sorting.length > 0) {
          const firstSort = updates.sorting[0];
          const columnId =
            firstSort.id as keyof typeof COLUMN_TO_SORT_FIELD_MAP;
          const sortField = COLUMN_TO_SORT_FIELD_MAP[columnId] || firstSort.id;
          newSearch.staffSortBy = sortField;
          newSearch.staffSortDirection = firstSort.desc ? "DESC" : "ASC";
        } else {
          newSearch.staffSortBy = "experienceCount";
          newSearch.staffSortDirection = "DESC";
        }
      }

      if (updates.filters !== undefined) {
        const filterKeys: (keyof ProfileQueryRequest)[] = [
          "searchColumn",
          "searchTerm",
          "department",
          "country",
          "seniority",
          "minF1ExperienceYears",
          "maxF1ExperienceYears",
          "minTotalExperienceYears",
          "maxTotalExperienceYears",
          "maxFakeProbability",
        ];

        filterKeys.forEach((key) => {
          let urlKey: string;
          if (key === "searchColumn") {
            urlKey = "staffSearchColumn";
          } else if (key === "searchTerm") {
            urlKey = "staffSearchTerm";
          } else {
            urlKey = `staff${key.charAt(0).toUpperCase() + key.slice(1)}`;
          }

          const value = updates.filters![key];
          if (value !== undefined && value !== null && value !== "") {
            if (key === "searchTerm" && Array.isArray(value)) {
              newSearch[urlKey] =
                value.length > 0 ? value.join("||") : undefined;
            } else {
              newSearch[urlKey] = String(value);
            }
          } else {
            delete newSearch[urlKey];
          }
        });
      }

      Object.keys(newSearch).forEach((key) => {
        if (newSearch[key] === undefined) {
          delete newSearch[key];
        }
      });

      navigate({
        search: newSearch as Partial<
          Record<keyof FileExplorerSearchParams, string>
        >,
        replace: true,
      });
    },
    [search, navigate]
  );

  return {
    pagination,
    sorting,
    filters,
    updateSearchParams,
  };
}
